import React, { useContext, useEffect, useState, FunctionComponent, useRef } from 'react';
import styled from 'styled-components';
import {
	PupillaryDistanceBaseProviderReceivedMessage,
	PupillaryDistanceBaseProviderResponseMessage,
	PupillaryDistanceProviderProps,
	TryOnBaseProviderReceivedMessage,
	TryOnBaseProviderResponseMessage,
	TryOnMode,
	TryOnProviderExchangeData,
	TryOnProviderProps,
	ValueOf,
	parseJSON
} from '../tryOnModels';
import {  useZakekeTryOn } from '../../../hooks/tryonContext';
import { TryOnFrame } from '../tryOnFrame';
import { useInternalState } from '../../../state/provider';

// #region interfaces
const MazingTryOnBaseReceivedMessage = {
	WaitingAuthentication: 'WAITING_AUTH',
	TryOnLoaded: 'MODEL_LOADED',
	ScreenShotTaken: 'SCREENSHOT_TAKEN',
	WebcamStopped: 'WEBCAM_STOPPED'
} as const;
export const MazingTryOnReceivedMessage = { ...TryOnBaseProviderReceivedMessage, ...MazingTryOnBaseReceivedMessage } as const;
export type MazingTryOnReceivedMessageType = ValueOf<typeof MazingTryOnReceivedMessage>;

const MazingTryOnBaseResponseMessage = {
	StartAuthenticationTryOn: 'AUTH_TRY_ON',
	LoadTryOn: 'LOAD_MODEL',
	TakeScreenshot: 'TAKE_SCREENSHOT',
	StopWebcam: 'STOP_WEBCAM'
} as const;
export const MazingTryOnResponseMessage = { ...TryOnBaseProviderResponseMessage, ...MazingTryOnBaseResponseMessage } as const;
export type MazingTryOnResponseMessageType = ValueOf<typeof MazingTryOnResponseMessage>;

const MazingPupillaryDistanceBaseStateReceivedMessage = {
	PDLoaded: 'PD_LOADED',
	PDUpdated: 'NEW_PD',
	PDDataNotFoundError: 'PD_ERROR',
	WebcamError: 'WEBCAM_ERROR',
	PDCountdown: 'PD_COUNTDOWN',
	WebcamStopped: 'WEBCAM_STOPPED'
} as const;

export const MazingPupillaryDistanceStateReceivedMessage = {
	...PupillaryDistanceBaseProviderReceivedMessage,
	...MazingPupillaryDistanceBaseStateReceivedMessage
} as const;
export type MazingPupillaryDistanceStateReceivedMessageType = ValueOf<typeof MazingPupillaryDistanceBaseStateReceivedMessage>;

const MazingPupillaryDistanceBaseResponseMessage = {
	StartAuthenticationPD: 'AUTH_TRY_ON',
	LoadPD: 'LOAD_PD',
	StartPD: 'START_PD',
	StopWebcam: 'STOP_WEBCAM'
} as const;
export const MazingPupillaryDistanceResponseMessage = {
	...PupillaryDistanceBaseProviderResponseMessage,
	...MazingPupillaryDistanceBaseResponseMessage
} as const;
export type MazingPupillaryDistanceResponseMessageType = ValueOf<typeof MazingPupillaryDistanceBaseResponseMessage>;

//#endregion

const MazingTryOnComponentContainer = styled.div`
	position: absolute;
	background-color: white;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
`;

// #region virtual try on

export const MazingTryOn: FunctionComponent<TryOnProviderProps> = (props) => {
	const internalState = useInternalState();
	const stateRef = useRef(internalState);
	stateRef.current = internalState;
  	const store = stateRef.current.state.environment;
	const [tryOnState, setTryOnState] = useState({} as TryOnProviderExchangeData);
	const { tryon, setTryon, tryOnPickedMode, setTryOnPickedMode } = useZakekeTryOn();
	const [frameWindow, setFrameWindow] = useState(null as Window | null);
	const frameWindowRef = useRef<Window | null>();
	frameWindowRef.current = frameWindow;
	const customerUid = parseJSON(props.customer.settings).uid;
	const productUid = parseJSON(props.product.settings).uid;
	const [switchedToPDTool, setSwitchedToPDTool] = useState(false);

	useEffect(() => {
		setTryon({
			...tryon,
			closeFrame: () => sendMessage(frameWindowRef.current!, MazingTryOnResponseMessage.StopWebcam, null),
			takeScreenshot: () => sendMessage(frameWindowRef.current!, MazingTryOnResponseMessage.TakeScreenshot, null),
			switchToPDTool: () => {
				setSwitchedToPDTool(true);
				sendMessage(frameWindowRef.current!, MazingPupillaryDistanceResponseMessage.LoadPD, null);
				tryon.changeMode?.(TryOnMode.PDTool);
			},
			switchToTryOn: () => {
				setSwitchedToPDTool(false);
				sendMessage(frameWindowRef.current!, MazingTryOnResponseMessage.LoadTryOn, {
					glb: props.obj,
					settings: props.fineTuningSettings
				});
				tryon.changeMode?.(TryOnMode.TryOn);
			},
			startPupillaryDistance: (time) => {
				sendMessage(frameWindowRef.current!, MazingPupillaryDistanceResponseMessage.StartPD, { seconds: time });
			}
		});
	}, []);

	useEffect(() => {
		switch (tryOnState.type) {
			// TRYON MESSAGES
			case MazingTryOnReceivedMessage.Start:
				break;
			case MazingTryOnReceivedMessage.WaitingAuthentication:
				sendMessage(frameWindow!, MazingTryOnResponseMessage.StartAuthenticationTryOn, {
					customer_uid: customerUid,
					product_uid: productUid
				});
				break;
			case MazingTryOnReceivedMessage.AuthSuccessful:
				sendMessage(frameWindow!, MazingTryOnResponseMessage.LoadTryOn, {
					glb: props.obj,
					settings: props.fineTuningSettings
				});
				break;
			case MazingTryOnReceivedMessage.TryOnLoaded:
				props.onLoaded?.();
				break;

			// PD MESSAGES
			case MazingPupillaryDistanceStateReceivedMessage.PDLoaded:
				props.onLoaded?.();
				break;
			case MazingPupillaryDistanceStateReceivedMessage.PDUpdated:
				store.setPDDistance(tryOnState.value?.pd);
				props.onPDUpdated?.(tryOnState.value?.pd);
				break;
			case MazingPupillaryDistanceStateReceivedMessage.PDDataNotFoundError:
				break;

			case MazingTryOnReceivedMessage.WebcamError:
				props.onWebcamError();
				break;
			case MazingTryOnReceivedMessage.WebcamStopped:
				props.onClose?.();
				break;
		}
	}, [tryOnState]);

	const sendMessage = (window: Window, message: MazingTryOnResponseMessageType | MazingPupillaryDistanceResponseMessageType, value: any) => {
		window.postMessage({ type: message, value: value }, '*');
	};

	const handleMessage = (e: MessageEvent) => {
		setTryOnState(e.data as TryOnProviderExchangeData);
	};

	return (
		<MazingTryOnComponentContainer className={`mazing-tryon ${(props.className) ? props.className : ""}`}>
			<TryOnFrame
				providerTitle='Mazing'
				providerUrl={Zakeke.config.mazingSettings.url}
				onReady={(w) => {
					setFrameWindow(w);
					props.onReady?.();
				}}
				outMessageHandler={handleMessage.bind(this)}
			/>
		</MazingTryOnComponentContainer>
	);
};

// #endregion

// #region pd tool
export const MazingPupillaryDistance: FunctionComponent<PupillaryDistanceProviderProps> = (props) => {
	const [pdState, setPDState] = useState({} as TryOnProviderExchangeData);
	const { tryon, setTryon } = useZakekeTryOn();
	const internalState = useInternalState();
	const stateRef = useRef(internalState);
	stateRef.current = internalState;
  	const store = stateRef.current.state.environment;

	const [frameWindow, setFrameWindow] = useState(null as Window | null);
	const frameWindowRef = useRef<Window | null>();
	frameWindowRef.current = frameWindow;

	const customerUid = parseJSON(props.customer.settings).uid;
	const productUid = parseJSON(props.product.settings).uid;

	useEffect(() => {
		setTryon({
			...tryon,
			closeFrame: () => sendMessage(frameWindowRef.current!, MazingPupillaryDistanceResponseMessage.StopWebcam, null),

			switchToPDTool: () => {
				console.log('switchToPDTool not active');
			},
			switchToTryOn: () => {
				console.log('switchToTryOn not active');
			},
			takeScreenshot: () => console.log('takeScreenshot not active'),
			startPupillaryDistance: (time) => sendMessage(frameWindowRef.current!, MazingPupillaryDistanceResponseMessage.StartPD, { seconds: time })
		});
	}, []);

	useEffect(() => {
		switch (pdState.type) {
			case MazingPupillaryDistanceStateReceivedMessage.Start:
				break;
			case MazingPupillaryDistanceStateReceivedMessage.WaitingAuthentication:
				sendMessage(frameWindow!, MazingPupillaryDistanceResponseMessage.StartAuthenticationPD, {
					customer_uid: customerUid,
					product_uid: productUid
				});
				break;
			case MazingPupillaryDistanceStateReceivedMessage.AuthSuccessful:
				sendMessage(frameWindow!, MazingPupillaryDistanceResponseMessage.LoadPD, {});
				break;
			case MazingPupillaryDistanceStateReceivedMessage.PDLoaded:
				props.onLoaded?.();
				break;
			case MazingPupillaryDistanceStateReceivedMessage.PDUpdated:
				store.setPDDistance(pdState.value.pd);
				props.onPDUpdated?.(pdState.value.pd);
				break;
			case MazingPupillaryDistanceStateReceivedMessage.PDDataNotFoundError:
				break;
			case MazingPupillaryDistanceStateReceivedMessage.WebcamError:
				props.onWebcamError();
				break;
			case MazingPupillaryDistanceStateReceivedMessage.WebcamStopped:
				props.onClose?.();
				break;
		}
	}, [pdState]);

	const sendMessage = (window: Window, message: MazingPupillaryDistanceResponseMessageType, value: any) => {
		window.postMessage({ type: message, value: value }, '*');
	};

	const handleMessage = (e: MessageEvent) => {
		if (!!e.data?.type) setPDState(e.data as TryOnProviderExchangeData);
	};

	return (
		<MazingTryOnComponentContainer className={`mazing-pd ${(props.className) ? props.className : ""}`}>
			<TryOnFrame
				providerTitle='mazing'
				providerUrl={Zakeke.config.mazingSettings.url}
				onReady={(w) => {
					setFrameWindow(w);
					props.onReady?.();
				}}
				outMessageHandler={handleMessage.bind(this)}
			/>
		</MazingTryOnComponentContainer>
	);
};
// #endregion

// MazingTryOnComponent.defaultProps = {
//   initialState: {
//     type: MazingTryOnState.Start,
//     value: null,
//   },
//   fineTuningSettings: {
//     position: {
//       x: 0,
//       y: 0,
//       z: 0,
//     },
//     scale: 0,
//     temple: true,
//     environment: TryOnFineTuningMazingEnvironment.Neutral,
//   },
// };

// MazingPDToolComponent.defaultProps = {
//   initialState: {
//     type: MazingTryOnState.Start,
//     value: null,
//   },
// };
